import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { SelectButtonModule } from "primeng/selectbutton";
import { TabsModule } from "primeng/tabs";
import { TextareaModule } from "primeng/textarea";
import { ToastModule } from "primeng/toast";
import { STATE_LIST } from "src/app/admin/constants/constants";
import { Products } from "src/app/models/products";
import { ProductsService } from "src/app/services/products.service";

@Component({
  standalone: true,
  selector: "app-product-form",
  templateUrl: "./product-form.component.html",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    SelectButtonModule,
    TabsModule,
    TextareaModule,
    ToastModule,
  ],
})
export class ProductFormComponent implements OnInit {
  productForm: UntypedFormGroup;
  product: Products;
  productId = "";
  pageTitle = "Nuovo prodotto";
  loading = false;
  saving = false;
  error = "";
  stateOptions = STATE_LIST;

  constructor(
    private fb: UntypedFormBuilder,
    private productsService: ProductsService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.productForm = this.fb.group({
      title: ["", Validators.required],
      page_title: [""],
      page_url: [""],
      status: [""],
      category_id: [""],
      works_id: [""],
      location_id: [""],
      employee_id: [""],
      is_active: ["0"],
      is_featured: ["0"],
      description: [""],
      description_1: [""],
      description_2: [""],
      page_seotitle: [""],
      page_seokeywords: [""],
      page_seodescription: [""],
    });

    this.route.paramMap.subscribe((params) => {
      this.productId = params.get("id") || "";
      this.pageTitle = this.isEdit ? "Modifica prodotto" : "Nuovo prodotto";

      if (this.isEdit) {
        this.loadProduct();
      }
    });
  }

  get isEdit(): boolean {
    return !!this.productId;
  }

  loadProduct(): void {
    this.loading = true;
    this.productsService.getProduct(this.productId).subscribe({
      next: (product) => {
        this.product = product;
        this.productForm.patchValue({
          title: product.title || "",
          page_title: product.page_title || "",
          page_url: product.page_url || "",
          status: product.status || "",
          category_id: product.category_id || "",
          works_id: product.works_id || "",
          location_id: product.location_id || "",
          employee_id: product.employee_id || "",
          is_active: this.toStateValue(product.is_active),
          is_featured: this.toStateValue(product.is_featured),
          description: product.description || "",
          description_1: product.description_1 || "",
          description_2: product.description_2 || "",
          page_seotitle: product.page_seotitle || "",
          page_seokeywords: product.page_seokeywords || "",
          page_seodescription: product.page_seodescription || "",
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err;
        this.loading = false;
      },
    });
  }

  saveProduct(): void {
    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }

    const formData = new FormData();
    Object.entries(this.productForm.value).forEach(([key, value]) => {
      formData.append(key, value === null || value === undefined ? "" : `${value}`);
    });

    this.saving = true;
    const request = this.isEdit
      ? this.productsService.updateProduct(formData, this.productId)
      : this.productsService.createProduct(formData);

    request.subscribe({
      next: (res) => {
        this.saving = false;
        if (res?.status === "error") {
          this.messageService.add({
            severity: "warn",
            summary: "Attenzione",
            detail: res.message || "Salvataggio non riuscito",
          });
          return;
        }

        this.messageService.add({
          severity: "success",
          summary: "Prodotto",
          detail: "Prodotto salvato con successo",
        });

        const savedId = res?.id || res?.ref_id || this.productId;
        if (!this.isEdit && savedId) {
          this.router.navigate(["/products", savedId]);
          return;
        }

        if (this.isEdit) {
          this.loadProduct();
        }
      },
      error: (err) => {
        this.error = err;
        this.saving = false;
      },
    });
  }

  private toStateValue(value: boolean | string | number): string {
    return value === true || value === "1" || value === 1 ? "1" : "0";
  }
}
